import { useBoardStore } from '../store/useBoardStore';
import { buildBoardDocumentOps } from './buildBoardDocumentOps';
import { hasFrameDocumentChange } from './boardDocumentChanges';
import { extractBoardDocumentSnapshot, framesFromEditor } from './boardSnapshot';
import { diffDocumentSnapshots } from './documentSnapshotDiff';

export { diffDocumentSnapshots } from './documentSnapshotDiff';

/** Keep Zustand's local snapshot in sync without PATCHing /snapshot. */
export function syncLocalBoardSnapshot(editor) {
  if (!editor) return null;
  const store = useBoardStore.getState();
  try {
    const snapshot = extractBoardDocumentSnapshot(editor);
    if (snapshot) store.updateSnapshotLocal(snapshot);
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * Persist document mutations as object-level /ops (+ frames-ops when frames change).
 * Never PATCHes full /snapshot — avoids wipe races with in-flight ops.
 *
 * @param {object} editor
 * @param {object|null} beforeSnapshot  extractBoardDocumentSnapshot before the mutation
 * @param {{ persistFrames?: boolean }} [options]
 */
export async function flushBoardDocumentOps(editor, beforeSnapshot, options = {}) {
  if (!editor) return { ops: [], framesSaved: false };

  const store = useBoardStore.getState();
  const before = beforeSnapshot ?? store.board?.tldrawSnapshot ?? { store: {} };
  const after = syncLocalBoardSnapshot(editor) || extractBoardDocumentSnapshot(editor);
  if (!after) return { ops: [], framesSaved: false };

  const changes = diffDocumentSnapshots(before, after);
  const ops = buildBoardDocumentOps(changes);

  if (ops.length) {
    try {
      await store.saveDocumentOps(ops);
    } catch (err) {
      console.warn('Failed to flush board document ops', err);
    }
  }

  const shouldSaveFrames =
    options.persistFrames !== false && hasFrameDocumentChange(changes);
  if (shouldSaveFrames) {
    try {
      await store.saveFrames(framesFromEditor(editor));
    } catch (err) {
      console.warn('Failed to flush board frames after ops', err);
    }
  }

  return { ops, framesSaved: shouldSaveFrames };
}

/**
 * Drop-in for `{ saveSnapshot }` callbacks that previously PATCHed /snapshot.
 * Diffs against the last known local snapshot and writes /ops (+ frames via editor).
 */
export function createOpsOnlyDocumentSaver(editor) {
  return async snapshot => {
    if (!snapshot) {
      if (editor) syncLocalBoardSnapshot(editor);
      return;
    }

    const store = useBoardStore.getState();
    const before = store.board?.tldrawSnapshot || { store: {} };
    store.updateSnapshotLocal(snapshot);

    const changes = diffDocumentSnapshots(before, snapshot);
    const ops = buildBoardDocumentOps(changes);
    if (ops.length) {
      try {
        await store.saveDocumentOps(ops);
      } catch (err) {
        console.warn('Failed to persist board document ops', err);
      }
    }

    if (editor && hasFrameDocumentChange(changes)) {
      try {
        await store.saveFrames(framesFromEditor(editor));
      } catch (err) {
        console.warn('Failed to persist board frames', err);
      }
    }
  };
}
